/* Article-only progressive enhancement; no network, storage or third-party dependencies. */
(() => {
  if (customElements.get('ichika-toc')) return;
  const normalize = text => text.normalize('NFC').replace(/\s+/gu, ' ').trim();
  const hash = text => {
    let value = 2166136261;
    for (const character of text) { value ^= character.codePointAt(0); value = Math.imul(value, 16777619); }
    return (value >>> 0).toString(36);
  };
  const unique = base => {
    let id = base, suffix = 2;
    while (document.getElementById(id)) id = `${base}-${suffix++}`;
    return id;
  };
  class IchikaToc extends HTMLElement {
    connectedCallback() {
      // defer script normally runs after parsing; editor insertion also supplies complete markup.
      if (!this.querySelector('[data-toc-content]')) return;
      this.dispose?.();
      const abort = new AbortController();
      const {signal} = abort;
      this.dispose = () => { abort.abort(); cancelAnimationFrame(this.frame); };
      const content = this.querySelector('[data-toc-content]');
      if (this.querySelector('[data-toc-nav]')) return;
      // Fail closed when the old app is still included; never remove another app's DOM.
      if (document.getElementById('ruffruff-table-of-contents-settings')) {
        this.dataset.tocStatus = 'legacy-app-active';
        return;
      }
      let legacy = [];
      try { legacy = JSON.parse(this.querySelector('[data-toc-legacy]')?.textContent || '[]'); } catch { /* content remains readable */ }
      const occurrence = new Map();
      const all = [...content.querySelectorAll('h2,h3')];
      const headings = all.filter(h => normalize(h.textContent) && !h.closest('[data-toc-exclude],nav,template'));
      const aliases = new Map();
      const targets = [];
      for (const heading of headings) {
        const text = normalize(heading.textContent);
        const key = `${heading.tagName}|${text}`;
        const count = (occurrence.get(key) || 0) + 1;
        occurrence.set(key, count);
        // Preserve the original first owner even when authored IDs collide elsewhere in the document.
        if (!heading.id || document.getElementById(heading.id) !== heading) {
          heading.id = unique(`ichika-heading-${hash(key)}-${count}`);
        }
        heading.classList.add('ichika-toc-target');
        const record = legacy.find(r => r.key === key && r.occurrence === count);
        if (record) {
          const owner = document.getElementById(record.alias);
          if (!owner) {
            const anchor = document.createElement('span');
            anchor.id = record.alias;
            anchor.className = 'ichika-toc-alias';
            anchor.setAttribute('aria-hidden', 'true');
            heading.prepend(anchor);
            aliases.set(anchor.id, heading);
          } else if (owner === heading || heading.contains(owner)) aliases.set(record.alias, heading);
          else this.dataset.tocAliasConflict = 'true';
        }
        if (heading.tagName === 'H2' || this.dataset.depth !== 'h2') targets.push({heading, text});
      }
      // Anchors remain available even on articles below the TOC display threshold.
      const offset = () => Math.max(0, parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0) + (Number(this.dataset.offset) || 0);
      const jump = (heading, animate = false, focus = false) => {
        if (focus) {
          const had = heading.hasAttribute('tabindex');
          if (!had) {
            heading.setAttribute('tabindex', '-1');
            heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), {once:true, signal});
          }
          heading.focus({preventScroll:true});
        }
        window.scrollTo({top: Math.max(0, window.scrollY + heading.getBoundingClientRect().top - offset()), behavior: animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant'});
      };
      const hashTarget = () => {
        let id;
        try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
        const target = aliases.get(id) || headings.find(h => h.id === id);
        if (target) jump(target);
      };
      window.addEventListener('hashchange', hashTarget, {signal});
      // Correct an incoming fragment after lazy images/fonts settle, but stop once the reader interacts.
      let incoming = Boolean(location.hash);
      for (const event of ['wheel','touchstart','pointerdown','keydown']) window.addEventListener(event, () => { incoming = false; }, {once:true, passive:true, signal});
      content.addEventListener('load', () => { if (incoming) hashTarget(); }, {capture:true, signal});
      window.addEventListener('load', () => { if (incoming) hashTarget(); }, {once:true, signal});
      document.fonts?.ready.then(() => { if (!signal.aborted && incoming) hashTarget(); });
      hashTarget();
      if (targets.length < (Number(this.dataset.minimum) || 2)) { this.dataset.tocStatus = 'below-minimum'; return; }
      const nav = document.createElement('nav');
      nav.dataset.tocNav = '';
      nav.className = 'ichika-toc-nav';
      nav.setAttribute('aria-label', this.dataset.title || '目次');
      const details = document.createElement('details');
      details.open = this.dataset.collapsed !== 'true';
      const summary = document.createElement('summary');
      summary.textContent = this.dataset.title || '目次';
      const list = document.createElement('ul');
      const links = [];
      let lastH2, children, childSummary, childCount = 0;
      for (const {heading, text} of targets) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(heading.id)}`;
        link.textContent = text;
        item.append(link);
        if (heading.tagName === 'H3' && lastH2) {
          if (!children) {
            const sectionDetails = document.createElement('details');
            sectionDetails.className = 'ichika-toc-section__details';
            childSummary = document.createElement('summary');
            children = document.createElement('ul');
            sectionDetails.append(childSummary, children);
            lastH2.append(sectionDetails);
            childCount = 0;
          }
          childCount += 1;
          childSummary.textContent = `詳細項目 (${childCount})`;
          children.append(item);
        } else {
          list.append(item);
          if (heading.tagName === 'H2') { lastH2 = item; children = null; childSummary = null; childCount = 0; }
        }
        link.addEventListener('click', event => {
          if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
          event.preventDefault();
          incoming = false;
          if (location.hash !== link.hash) history.pushState(null, '', link.hash);
          jump(heading, this.dataset.smooth === 'true', true);
        }, {signal});
        links.push(link);
      }
      details.append(summary, list); nav.append(details);
      if (this.dataset.position === 'top') content.prepend(nav);
      else {
        // Avoid inserting a block nav inside an inline wrapper or another heading.
        let insertion = targets[0].heading;
        while (insertion.parentElement !== content && insertion.parentElement) insertion = insertion.parentElement;
        insertion.before(nav);
      }
      const update = () => {
        this.frame = 0;
        const line = offset() + 2;
        let active = -1;
        targets.forEach(({heading}, i) => { if (heading.getBoundingClientRect().top <= line) active = i; });
        links.forEach((link, i) => { if (i === active) link.setAttribute('aria-current', 'location'); else link.removeAttribute('aria-current'); });
      };
      const schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(update); };
      window.addEventListener('scroll', schedule, {passive:true, signal});
      window.addEventListener('resize', schedule, {passive:true, signal});
      details.addEventListener('toggle', schedule, {signal});
      update();
      if (incoming) hashTarget();
      this.dataset.tocStatus = 'ready';
    }
    disconnectedCallback() { this.dispose?.(); this.querySelector('[data-toc-nav]')?.remove(); }
  }
  customElements.define('ichika-toc', IchikaToc);
})();

